import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Paper, Snackbar, Stack, Tabs, Tab, Typography } from '@mui/material';
import {
    Download as DownloadIcon,
    FolderOpen as FolderOpenIcon,
    DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import ModeListSettings from './ModeListSettings';
import ConnectionSettings from './ConnectionSettings';
import {
    loadModeFilter,
    saveModeFilter,
    STORE_KEY as MODE_FILTER_STORE_KEY,
} from './mode_catalog';
import {
    loadWsPresets,
    saveWsPresets,
    STORE_KEY as WS_PRESETS_STORE_KEY,
} from './connection_presets';

const SECTIONS = [
    { id: 'connection', label: 'Connection', render: () => <ConnectionSettings /> },
    { id: 'mode-list', label: 'Mode List', render: () => <ModeListSettings /> },
];

// Map each persisted settings key to its (apply, clear) helpers so that
// importing/clearing stays in lock-step with the rest of the app's stores.
const SETTINGS_HANDLERS = {
    [MODE_FILTER_STORE_KEY]: {
        apply: (value) => saveModeFilter(value && typeof value === 'object' ? value : {}),
        clear: () => saveModeFilter({}),
    },
    [WS_PRESETS_STORE_KEY]: {
        apply: (value) => saveWsPresets(Array.isArray(value) ? value : []),
        clear: () => saveWsPresets([]),
    },
};

const handleExport = () => {
    const payload = {
        version: 1,
        exportedAt: new Date().toISOString(),
        settings: {
            [MODE_FILTER_STORE_KEY]: loadModeFilter(),
            [WS_PRESETS_STORE_KEY]: loadWsPresets(),
        },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    a.download = `lite-gcs-settings-${ts}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
};

const SettingsPage = () => {
    const [activeId, setActiveId] = useState(SECTIONS[0]?.id || null);
    const active = SECTIONS.find((s) => s.id === activeId) || SECTIONS[0];
    const fileInputRef = useRef(null);
    const [toast, setToast] = useState(null); // { severity, message }

    const showToast = (severity, message) => setToast({ severity, message });

    const handleImportClick = () => {
        if (fileInputRef.current) fileInputRef.current.value = '';
        fileInputRef.current?.click();
    };

    const handleImportFile = async (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;
        try {
            const text = await file.text();
            const parsed = JSON.parse(text);
            const settings = parsed && typeof parsed === 'object' ? parsed.settings : null;
            if (!settings || typeof settings !== 'object') {
                throw new Error('Missing "settings" object');
            }
            const applied = [];
            Object.entries(settings).forEach(([key, value]) => {
                const handler = SETTINGS_HANDLERS[key];
                if (handler) {
                    handler.apply(value);
                    applied.push(key);
                }
            });
            if (applied.length === 0) {
                showToast('warning', 'No recognized settings found in file.');
            } else {
                showToast('success', `Loaded settings: ${applied.join(', ')}`);
            }
        } catch (err) {
            console.error('Settings import failed', err);
            showToast('error', `Import failed: ${err.message || err}`);
        }
    };

    const handleClear = () => {
        // eslint-disable-next-line no-alert
        const ok = window.confirm('Clear all Lite GCS settings? This will reset Connection presets and Mode List filters to defaults.');
        if (!ok) return;
        Object.values(SETTINGS_HANDLERS).forEach((h) => {
            try { h.clear(); } catch (err) { console.warn('clear failed', err); }
        });
        showToast('success', 'Settings cleared.');
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mx: 2, mt: 2, borderBottom: 1, borderColor: 'divider' }}
            >
                {SECTIONS.length > 0 && (
                    <Tabs value={activeId} onChange={(_, v) => setActiveId(v)} variant="standard" sx={{ flex: 1, minHeight: 36 }}>
                        {SECTIONS.map((s) => (
                            <Tab key={s.id} value={s.id} label={s.label} />
                        ))}
                    </Tabs>
                )}
                <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleClear}
                    sx={{ mb: 0.5 }}
                >
                    Clear
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExport}
                    sx={{ mb: 0.5 }}
                >
                    Save
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FolderOpenIcon />}
                    onClick={handleImportClick}
                    sx={{ mb: 0.5 }}
                >
                    Load
                </Button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/json,.json"
                    style={{ display: 'none' }}
                    onChange={handleImportFile}
                />
            </Stack>
            <Box
                sx={{ flex: 1, minHeight: 0 }}
                style={{ display: 'flex', flexDirection: 'column', margin: 2 }}
            >
                <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}>
                    {active ? active.render() : (
                        <Typography variant="body2" color="text.secondary">
                            No settings available.
                        </Typography>
                    )}
                </Box>
            </Box>
            <Snackbar
                open={!!toast}
                autoHideDuration={3500}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {toast ? (
                    <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)} sx={{ width: '100%' }}>
                        {toast.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </div>
    );
};

export default SettingsPage;

